import React, { useState, useEffect } from 'react';
import {
  Card,
  Button,
  Table,
  Modal,
  TextInput,
  Select,
  Alert,
  Spinner,
  Badge,
  Pagination,
  Datepicker,
} from 'flowbite-react';
import Select2 from 'react-select';
import { fetchWithAuth } from '../utils/api';
import {
  getBigqueryEndpoints,
  getExternalSourceEndpoints,
} from '../config/api';
import {
  getValidationMessages,
  formatCurrency,
  formatDate,
} from '../utils/helper';
import { loadMarketersList } from '../config/lib';

// Source constants
export const SOURCE_TYPES = {
  FACEBOOK: 'Facebook',
  GOOGLE: 'Google',
  TIKTOK: 'TikTok',
  ZALO: 'Zalo',
} as const;

export type SourceType =
  | typeof SOURCE_TYPES.FACEBOOK
  | typeof SOURCE_TYPES.GOOGLE
  | typeof SOURCE_TYPES.TIKTOK;

interface Marketer {
  id: number;
  name: string;
}

interface ExternalSource {
  id: number;
  name: string;
  total_lead: number | null;
  total_order: number | null;
  revenue: number | null;
  revenue_date: string;
  source: SourceType;
  marketer: number | null;
  marketer_name?: string;
  created_at: string;
}

interface ExternalSourceFormData {
  name: string;
  total_lead: number | null;
  total_order: number | null;
  revenue: number | null;
  revenue_date: string;
  source: SourceType;
  marketer: number | null;
}

const ExternalSource: React.FC = () => {
  const [sources, setSources] = useState<ExternalSource[]>([]);
  const [marketers, setMarketers] = useState<Marketer[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<{
    type: 'success' | 'warning' | 'failure';
    text: string;
  } | null>(null);
  const [messagePopup, setMessagePopup] = useState<{
    type: 'success' | 'warning' | 'failure';
    text: string;
  } | null>(null);

  // Search state
  const [searchTerm, setSearchTerm] = useState('');

  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const itemsPerPage = 10;

  // Modal states
  const [showModal, setShowModal] = useState(false);
  const [modalMode, setModalMode] = useState<'create' | 'edit'>('create');
  const [editingSource, setEditingSource] = useState<ExternalSource | null>(
    null
  );

  const [bigQueryDate, setBigQueryDate] = useState<Date>(new Date());

  // Get yesterday's date in YYYY-MM-DD format
  const getYesterdayDate = (): string => {
    const yesterday = new Date();
    yesterday.setDate(yesterday.getDate() - 1);
    return formatDate(yesterday);
  };

  // Form states
  const [formData, setFormData] = useState<ExternalSourceFormData>({
    name: '',
    total_lead: null,
    total_order: null,
    revenue: null,
    revenue_date: getYesterdayDate(),
    source: SOURCE_TYPES.FACEBOOK,
    marketer: null,
  });

  const [formErrors, setFormErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Load sources
  const loadSources = async (page = 1) => {
    try {
      setLoading(true);
      setError(null);

      const sourcesEndpoint = getExternalSourceEndpoints().LIST;
      const params = new URLSearchParams({
        page: page.toString(),
        page_size: itemsPerPage.toString(),
        ...(searchTerm && { search: searchTerm }),
      });

      const response = await fetchWithAuth(`${sourcesEndpoint}?${params}`);

      if (response.success && response.data) {
        setSources(response.data.results || []);
        setTotalPages(Math.ceil((response.data.count || 0) / itemsPerPage));
        setTotalItems(response.data.count || 0);
      } else {
        setError('Không thể tải danh sách external source');
      }
    } catch (err) {
      setError('Đã xảy ra lỗi khi tải dữ liệu');
      console.error('Load sources error:', err);
    } finally {
      setLoading(false);
    }
  };

  // Load marketers
  const loadMarketers = async () => {
    const marketersList = await loadMarketersList();
    setMarketers(marketersList);
  };

  useEffect(() => {
    loadSources();
    loadMarketers();
  }, []);

  // Handle search
  const handleSearch = () => {
    setCurrentPage(1);
    loadSources(1);
  };

  // Handle pagination
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
    loadSources(page);
  };

  // Open create modal
  const openCreateModal = () => {
    setModalMode('create');
    setEditingSource(null);
    setFormData({
      name: '',
      total_lead: null,
      total_order: null,
      revenue: null,
      revenue_date: getYesterdayDate(),
      source: SOURCE_TYPES.FACEBOOK,
      marketer: null,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Open edit modal
  const openEditModal = (source: ExternalSource) => {
    setModalMode('edit');
    setEditingSource(source);
    setFormData({
      name: source.name,
      total_lead: source.total_lead,
      total_order: source.total_order,
      revenue: source.revenue,
      revenue_date: source.revenue_date,
      source: source.source,
      marketer: source.marketer,
    });
    setFormErrors({});
    setShowModal(true);
    setMessagePopup(null);
  };

  // Handle form input change
  const handleInputChange = (
    field: keyof ExternalSourceFormData,
    value: any
  ) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    if (formErrors[field]) {
      setFormErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const errors: Record<string, string> = {};

    if (!formData.name.trim()) {
      errors.name = 'Tên là bắt buộc';
    }

    if (!formData.marketer) {
      errors.marketer = 'Marketer là bắt buộc';
    }

    if (!formData.total_lead) {
      errors.total_lead = 'Tổng LEAD là bắt buộc';
    }

    if (!formData.total_order) {
      errors.total_order = 'Tổng đơn hàng là bắt buộc';
    }

    if (!formData.revenue) {
      errors.revenue = 'Doanh thu là bắt buộc';
    }

    if (formData.total_lead && formData.total_lead < 0) {
      errors.total_lead = 'Tổng LEAD phải lớn hơn hoặc bằng 0';
    }

    if (formData.total_order && formData.total_order < 0) {
      errors.total_order = 'Tổng đơn hàng phải lớn hơn hoặc bằng 0';
    }

    if (formData.revenue && formData.revenue < 0) {
      errors.revenue = 'Doanh thu phải lớn hơn hoặc bằng 0';
    }

    if (!formData.revenue_date) {
      errors.revenue_date = 'Ngày tính doanh thu là bắt buộc';
    }

    if (formData.revenue_date && new Date(formData.revenue_date) > new Date()) {
      errors.revenue_date =
        'Ngày tính doanh thu phải nhỏ hơn hoặc bằng ngày hiện tại';
    }

    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setSubmitting(true);
    setMessage(null);

    try {
      const sourcesEndpoint = getExternalSourceEndpoints();
      const endpoint =
        modalMode === 'create'
          ? sourcesEndpoint.CREATE
          : `${sourcesEndpoint.UPDATE}/${editingSource?.id}`;

      const method = modalMode === 'create' ? 'POST' : 'PUT';

      const response = await fetchWithAuth(endpoint, {
        method,
        body: JSON.stringify({
          name: formData.name.trim(),
          total_lead: formData.total_lead,
          total_order: formData.total_order,
          revenue: formData.revenue,
          revenue_date: formData.revenue_date,
          source: formData.source.toLowerCase(),
          marketer: formData.marketer,
        }),
      });

      if (response.success) {
        setMessagePopup({
          type: 'success',
          text: `External Source ${
            modalMode === 'create' ? 'đã được tạo' : 'đã được cập nhật'
          } thành công!`,
        });
        setShowModal(false);
        loadSources(currentPage);
      } else {
        setMessagePopup({
          type: 'failure',
          text:
            getValidationMessages(response.data || {}) ||
            `Không thể ${
              modalMode === 'create' ? 'tạo' : 'cập nhật'
            } external source`,
        });
      }
    } catch (error) {
      setMessagePopup({
        type: 'failure',
        text: `Đã xảy ra lỗi khi ${
          modalMode === 'create' ? 'tạo' : 'cập nhật'
        } external source`,
      });
      console.error('Submit source error:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const [showModalConfirmDeleteData, setShowModalConfirmDeleteData] =
    useState(false);
  // Handle delete
  const handleDelete = async (sourceId: string) => {
    if (!window.confirm('Bạn có chắc chắn muốn xóa external source này?')) {
      return;
    }

    try {
      const sourcesEndpoint = getExternalSourceEndpoints();
      const response = await fetchWithAuth(
        `${sourcesEndpoint.DELETE}/${sourceId}`,
        {
          method: 'DELETE',
        }
      );

      if (response.success) {
        setMessage({
          type: 'success',
          text: 'External Source đã được xóa thành công!',
        });
        loadSources(currentPage);
      } else {
        setMessage({
          type: 'failure',
          text: 'Không thể xóa external source',
        });
      }
    } catch (error) {
      setMessage({
        type: 'failure',
        text: 'Đã xảy ra lỗi khi xóa external source',
      });
      console.error('Delete source error:', error);
    }
  };

  const removeNonNumeric = (value: string) => {
    return value.replace(/[^0-9]/g, '');
  };

  const [showModalConfirmPushData, setShowModalConfirmPushData] =
    useState(false);

  const pushDataToBigQuery = async () => {
    try {
      setMessage({
        type: 'warning',
        text: `Đang đẩy dữ liệu lên BigQuery trong ngày ${formatDate(
          bigQueryDate
        )}... Chờ cho quá trình hoàn thành...`,
      });
      setShowModalConfirmPushData(false);
      const response = await fetchWithAuth(
        getBigqueryEndpoints().PUSH_REVENUE,
        {
          method: 'POST',
          body: JSON.stringify({
            date: formatDate(bigQueryDate),
          }),
        }
      );
      if (response.success) {
        setMessage({
          type: 'success',
          text:
            response.message ||
            `Đẩy dữ liệu lên BigQuery trong ngày ${formatDate(
              bigQueryDate
            )} thành công!`,
        });
      } else {
        setMessage({
          type: 'failure',
          text:
            response.error ||
            `Đã xảy ra lỗi khi đẩy dữ liệu lên BigQuery trong ngày ${formatDate(
              bigQueryDate
            )}!`,
        });
      }
    } catch (error) {
      console.error('Push data to BigQuery error:', error);
      setMessage({
        type: 'failure',
        text: `Đã xảy ra lỗi khi đẩy dữ liệu lên BigQuery trong ngày ${formatDate(
          bigQueryDate
        )}!`,
      });
    }
  };

  const handleDeleteData = async () => {
    try {
      setMessage({
        type: 'warning',
        text: `Đang xóa dữ liệu đã tải trong ngày ${formatDate(
          bigQueryDate
        )}... Vui lòng chờ cho quá trình hoàn thành...`,
      });
      setShowModalConfirmDeleteData(false);
      const response = await fetchWithAuth(
        `${getBigqueryEndpoints().DELETE_PUSHED_DATA}?date=${formatDate(
          bigQueryDate
        )}`,
        {
          method: 'DELETE',
        }
      );
      if (response.success) {
        setMessage({
          type: 'success',
          text:
            response.message ||
            `Dữ liệu đã tải trong ngày ${formatDate(
              bigQueryDate
            )} đã được xóa thành công!`,
        });
      } else {
        setMessage({
          type: 'failure',
          text:
            response.error ||
            `Đã xảy ra lỗi khi xóa dữ liệu đã tải trong ngày ${formatDate(
              bigQueryDate
            )}!`,
        });
      }
    } catch (error) {
      console.error('Delete data today error:', error);
      setMessage({
        type: 'failure',
        text: `Đã xảy ra lỗi khi xóa dữ liệu đã tải trong ngày ${formatDate(
          bigQueryDate
        )}!`,
      });
    }
  };

  if (loading && sources.length === 0) {
    return (
      <div className="p-6 flex items-center justify-center min-h-screen">
        <div className="text-center">
          <Spinner size="xl" />
          <p className="mt-4 text-gray-600">
            Đang tải danh sách external source...
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Nguồn ngoài</h1>
            <p className="text-gray-600">
              Quản lý các nguồn quảng cáo bên ngoài
            </p>
          </div>
          {/* Modal Push Data  */}
          <Modal
            show={showModalConfirmPushData}
            onClose={() => setShowModalConfirmPushData(false)}
            size="4xl"
          >
            <Modal.Header>
              <p className="text-lg font-medium text-gray-900">
                Đẩy dữ liệu lên BigQuery
              </p>
            </Modal.Header>
            <Modal.Body className="min-h-[500px]">
              {loading ? (
                <div className="flex items-center justify-center">
                  <p className="text-yellow-500">
                    Đang đẩy dữ liệu lên BigQuery... Chờ cho quá trình hoàn
                    thành...
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-gray-600">
                    Bạn có chắc chắn muốn đẩy dữ liệu lên BigQuery?
                  </p>
                  <p className="text-red-500">
                    Hành động này không thể hoàn tác
                  </p>
                  <Datepicker
                    value={formatDate(bigQueryDate)}
                    defaultDate={bigQueryDate}
                    icon={undefined}
                    onSelectedDateChanged={e => {
                      setBigQueryDate(e);
                    }}
                    theme={{
                      views: {
                        days: {
                          items: {
                            base: 'grid w-64 grid-cols-7',
                            item: {
                              base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                              selected:
                                'bg-primary-700 text-blue-500 hover:bg-primary-600',
                              disabled: 'text-gray-500 line-through',
                            },
                          },
                        },
                      },
                    }}
                  />
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={() => setShowModalConfirmPushData(false)}
                className="btn-secondary"
                disabled={loading}
              >
                Hủy
              </Button>
              <Button
                onClick={pushDataToBigQuery}
                className="btn-primary"
                disabled={loading}
              >
                Đẩy dữ liệu
              </Button>
            </Modal.Footer>
          </Modal>

          {/* Modal Delete Data  */}
          <Modal
            show={showModalConfirmDeleteData}
            onClose={() => setShowModalConfirmDeleteData(false)}
            size="4xl"
          >
            <Modal.Header>
              <p className="text-lg font-medium text-gray-900">
                Xóa dữ liệu đã tải trong ngày {formatDate(bigQueryDate)}
              </p>
            </Modal.Header>
            <Modal.Body className="min-h-[500px]">
              {loading ? (
                <div className="flex items-center justify-center">
                  <p className="text-yellow-500">
                    Đang xóa dữ liệu đã tải trong ngày{' '}
                    {formatDate(bigQueryDate)}... Chờ cho quá trình hoàn
                    thành...
                  </p>
                </div>
              ) : (
                <>
                  <p className="text-gray-600">
                    Bạn có chắc chắn muốn xóa dữ liệu đã tải trong ngày
                    {formatDate(bigQueryDate)} không?
                  </p>
                  <p className="text-red-500">
                    Hành động này không thể hoàn tác
                  </p>
                  <Datepicker
                    value={formatDate(bigQueryDate)}
                    defaultDate={bigQueryDate}
                    icon={undefined}
                    onSelectedDateChanged={e => {
                      setBigQueryDate(e);
                    }}
                    theme={{
                      views: {
                        days: {
                          items: {
                            base: 'grid w-64 grid-cols-7',
                            item: {
                              base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                              selected:
                                'bg-primary-700 text-blue-500 hover:bg-primary-600',
                              disabled: 'text-gray-500 line-through',
                            },
                          },
                        },
                      },
                    }}
                  />
                </>
              )}
            </Modal.Body>
            <Modal.Footer>
              <Button
                onClick={() => setShowModalConfirmPushData(false)}
                className="btn-secondary"
                disabled={loading}
              >
                Hủy
              </Button>
              <Button
                onClick={handleDeleteData}
                className="btn-primary"
                disabled={loading}
              >
                Xóa dữ liệu
              </Button>
            </Modal.Footer>
          </Modal>

          <div className="flex space-x-2">
            <Button onClick={openCreateModal} className="btn-primary">
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M12 4v16m8-8H4"
                />
              </svg>
              Tạo External Source
            </Button>
            <Button
              className="btn-secondary"
              onClick={() => setShowModalConfirmPushData(true)}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M8 7l4-4m0 0l4 4m-4-4v18"
                />
              </svg>
              Đẩy dữ liệu lên BigQuery
            </Button>
            <Button
              className="btn-danger"
              onClick={() => setShowModalConfirmDeleteData(true)}
            >
              <svg
                className="w-4 h-4 mr-2"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                />
              </svg>
              Xóa dữ liệu đã tải
            </Button>
          </div>
        </div>
      </div>

      {message && (
        <Alert color={message.type} className="mb-4">
          <span>{message.text}</span>
        </Alert>
      )}

      {/* Search */}
      <Card className="mb-6">
        <div className="p-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <TextInput
              placeholder="Tìm kiếm theo tên..."
              value={searchTerm}
              onChange={e => setSearchTerm(e.target.value)}
            />
            <Button onClick={handleSearch} className="btn-primary">
              Tìm kiếm
            </Button>
          </div>
        </div>
      </Card>

      {/* Sources Table */}
      <Card>
        <div className="overflow-x-auto">
          <Table>
            <Table.Head>
              <Table.HeadCell>ID</Table.HeadCell>
              <Table.HeadCell>Hành động</Table.HeadCell>
              <Table.HeadCell>Tên</Table.HeadCell>
              <Table.HeadCell>Nguồn</Table.HeadCell>
              <Table.HeadCell>Marketer</Table.HeadCell>
              <Table.HeadCell>Tổng LEAD</Table.HeadCell>
              <Table.HeadCell>Tổng đơn hàng</Table.HeadCell>
              <Table.HeadCell>Doanh thu</Table.HeadCell>
              <Table.HeadCell>Ngày tính doanh thu</Table.HeadCell>
              <Table.HeadCell>Ngày tạo</Table.HeadCell>
            </Table.Head>
            <Table.Body className="divide-y">
              {sources.map(source => (
                <Table.Row
                  key={source.id}
                  className="bg-white dark:border-gray-700 dark:bg-gray-800"
                >
                  <Table.Cell className="whitespace-nowrap font-medium text-gray-900 dark:text-white">
                    {source.id}
                  </Table.Cell>
                  <Table.Cell>
                    <div className="flex space-x-2">
                      <Button
                        size="sm"
                        color="blue"
                        onClick={() => openEditModal(source)}
                      >
                        Sửa
                      </Button>
                      <Button
                        size="sm"
                        color="red"
                        onClick={() => handleDelete(source.id.toString())}
                      >
                        Xóa
                      </Button>
                    </div>
                  </Table.Cell>
                  <Table.Cell className="font-medium text-gray-900 dark:text-white">
                    {source.name}
                  </Table.Cell>
                  <Table.Cell>
                    <Badge
                      color={
                        source.source === SOURCE_TYPES.FACEBOOK
                          ? 'blue'
                          : source.source === SOURCE_TYPES.GOOGLE
                          ? 'red'
                          : 'purple'
                      }
                    >
                      {source.source.charAt(0).toUpperCase() +
                        source.source.slice(1)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="blue">
                      {source.marketer_name || `Marketer ${source.marketer}`}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="green">{source.total_lead}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="blue">{source.total_order}</Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    <Badge color="yellow">
                      {formatCurrency(source.revenue || 0)}
                    </Badge>
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(source.revenue_date)}
                  </Table.Cell>
                  <Table.Cell className="text-gray-500 dark:text-gray-400">
                    {formatDate(source.created_at)}
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="flex items-center justify-between p-4">
            <div className="text-sm text-gray-700">
              Hiển thị {(currentPage - 1) * itemsPerPage + 1} đến{' '}
              {Math.min(currentPage * itemsPerPage, totalItems)} trong tổng số{' '}
              {totalItems} kết quả
            </div>
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              onPageChange={handlePageChange}
            />
          </div>
        )}
      </Card>

      {/* Create/Edit Modal */}
      <Modal show={showModal} onClose={() => setShowModal(false)} size="4xl">
        <Modal.Header>
          {modalMode === 'create'
            ? 'Tạo External Source Mới'
            : 'Chỉnh Sửa External Source'}
        </Modal.Header>
        <form onSubmit={handleSubmit}>
          <Modal.Body>
            {messagePopup && (
              <Alert color={messagePopup.type} className="mb-4">
                <span>{messagePopup.text}</span>
              </Alert>
            )}
            <div className="space-y-4">
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tên *
                </label>
                <TextInput
                  value={formData.name}
                  onChange={e => handleInputChange('name', e.target.value)}
                  placeholder="Nhập tên external source"
                  color={formErrors.name ? 'failure' : undefined}
                  helperText={formErrors.name}
                />
              </div>

              {/* Marketer */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Marketer *
                </label>
                <Select2
                  value={
                    formData.marketer
                      ? {
                          value: formData.marketer,
                          label:
                            marketers.find(
                              marketer => marketer.id === formData.marketer
                            )?.name || '',
                        }
                      : null
                  }
                  onChange={e =>
                    handleInputChange(
                      'marketer',
                      parseInt(e?.value?.toString() || '0')
                    )
                  }
                  options={marketers.map(marketer => ({
                    value: marketer.id,
                    label: marketer.name,
                  }))}
                  isClearable
                  placeholder="Chọn Marketer..."
                />
                {formErrors.marketer && (
                  <p className="mt-1 text-sm text-red-600">
                    {formErrors.marketer}
                  </p>
                )}
              </div>

              {/* Source */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Nguồn *
                </label>
                <Select
                  value={formData.source}
                  onChange={e =>
                    handleInputChange('source', e.target.value as SourceType)
                  }
                >
                  <option value={SOURCE_TYPES.FACEBOOK}>
                    {SOURCE_TYPES.FACEBOOK}
                  </option>
                  <option value={SOURCE_TYPES.GOOGLE}>
                    {SOURCE_TYPES.GOOGLE}
                  </option>
                  <option value={SOURCE_TYPES.TIKTOK}>
                    {SOURCE_TYPES.TIKTOK}
                  </option>
                </Select>
              </div>

              {/* Total Lead and Total Order */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tổng LEAD *
                  </label>
                  <TextInput
                    type="text"
                    value={formData.total_lead || ''}
                    onChange={e =>
                      handleInputChange(
                        'total_lead',
                        removeNonNumeric(e.target.value)
                      )
                    }
                    color={formErrors.total_lead ? 'failure' : undefined}
                    helperText={formErrors.total_lead}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Tổng đơn hàng *
                  </label>
                  <TextInput
                    type="text"
                    value={formData.total_order || ''}
                    onChange={e =>
                      handleInputChange(
                        'total_order',
                        removeNonNumeric(e.target.value)
                      )
                    }
                    color={formErrors.total_order ? 'failure' : undefined}
                    helperText={formErrors.total_order}
                  />
                </div>
              </div>

              {/* Revenue */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Doanh thu *
                  </label>
                  <TextInput
                    type="text"
                    value={formData.revenue || ''}
                    onChange={e =>
                      handleInputChange(
                        'revenue',
                        removeNonNumeric(e.target.value)
                      )
                    }
                    color={formErrors.revenue ? 'failure' : undefined}
                    helperText={formErrors.revenue}
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Ngày tính doanh thu *
                  </label>
                  <Datepicker
                    value={formData.revenue_date}
                    defaultDate={new Date(formData.revenue_date)}
                    icon={undefined}
                    onSelectedDateChanged={e => {
                      handleInputChange('revenue_date', formatDate(e));
                    }}
                    color={formErrors.revenue_date ? 'failure' : undefined}
                    helperText={formErrors.revenue_date}
                    maxDate={new Date()}
                    theme={{
                      views: {
                        days: {
                          items: {
                            base: 'grid w-64 grid-cols-7',
                            item: {
                              base: 'block flex-1 cursor-pointer rounded-lg border-0 text-center text-sm font-semibold leading-9 text-gray-900 hover:bg-gray-100',
                              selected:
                                'bg-primary-700 text-blue-500 hover:bg-primary-600',
                              disabled: 'text-gray-500 line-through',
                            },
                          },
                        },
                      },
                    }}
                  />
                </div>
              </div>
            </div>
          </Modal.Body>
          <Modal.Footer>
            <Button
              type="button"
              color="gray"
              onClick={() => setShowModal(false)}
            >
              Hủy
            </Button>
            <Button type="submit" disabled={submitting} className="btn-primary">
              {submitting ? (
                <>
                  <Spinner size="sm" className="mr-2" />
                  Đang xử lý...
                </>
              ) : modalMode === 'create' ? (
                'Tạo External Source'
              ) : (
                'Cập nhật'
              )}
            </Button>
          </Modal.Footer>
        </form>
      </Modal>
    </div>
  );
};

export default ExternalSource;
